<script>
import bem from '@/utils/bem';
import PencilIcon from '@/components/svg/PencilIcon/PencilIcon.vue';
import EditTitleModal from '@/components/pageContent/modules/EditTitleModal.vue';
import EditSubtitlesModal from '@/components/pageContent/modules/EditSubtitlesModal.vue';
import ImageUploadModal from '@/components/pageContent/modules/ImageUploadModal.vue';
import EditButtonModal from '@/components/pageContent/modules/EditButtonModal.vue';
import publishGuardMixin from '@/mixins/publishGuardMixin';

export default {
  mixins: [publishGuardMixin],
  name: 'TrackAnytimeTab',
  components: {
    PencilIcon,
    EditTitleModal,
    EditSubtitlesModal,
    ImageUploadModal,
    EditButtonModal,
  },
  props: {
    title: {
      type: String,
      default: 'Your Project Status Is Always A Click Away.',
    },
    titleMob: {
      type: String,
      default: '',
    },
    descriptions: {
      type: Array,
      default: () => [
        {
          id: '1',
          title: '',
          text: 'You can easily check the exact status of your project by simply logging into your own personal account. From the moment your media is received by our team through when your project lands back on your doorstep, you\'ll be informed.\n\nThat\'s because every one of our client projects is meticulously tracked down to the most minute details by our very own Project Management system, which we designed and scripted in-house to ensure that your items are accounted for at all times.',
        },
      ],
    },
    imageSrc: {
      type: String,
      default: '',
    },
    imageSrcMobile: {
      type: String,
      default: '',
    },
    isLeft: {
      type: Boolean,
      default: false,
    },
    linkUrl: {
      type: String,
      default: '#',
    },
    linkText: {
      type: String,
      default: 'Visit Our Forever Care Support Center',
    },
    withLink: {
      type: Boolean,
      default: false,
    },
    isMobileView: {
      type: Boolean,
      default: false,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update'],
  data() {
    return {
      // Modal state
      titleModalVisible: false,
      descriptionsModalVisible: false,
      imageModalVisible: false,
      linkModalVisible: false,
    };
  },
  computed: {
    displayTitle() {
      return this.isMobileView ? (this.titleMob || this.title) : this.title;
    },
    subtitlesData() {
      // Convert descriptions to format expected by EditSubtitlesModal
      return this.descriptions.map((desc) => ({
        title: desc.title || '',
        description: desc.text || '',
      }));
    },
    hasLink() {
      return this.withLink && !!this.linkText;
    },
  },
  methods: {
    b: bem('track-anytime-tab'),
    // Title modal
    openTitleModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.titleModalVisible = true;
      }
    },
    handleTitleSave(values) {
      this.$emit('update', {
        title: values.title,
        titleMob: values.titleMobile,
      });
      this.titleModalVisible = false;
    },
    handleTitleDelete() {
      this.$emit('update', {
        title: '',
        titleMob: '',
      });
      this.titleModalVisible = false;
    },
    // Descriptions modal
    openDescriptionsModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.descriptionsModalVisible = true;
      }
    },
    handleDescriptionsSave(items) {
      // Convert from EditSubtitlesModal format back to descriptions format
      const newDescriptions = items.map((item, index) => ({
        id: this.descriptions[index]?.id || String(index + 1),
        title: item.title,
        text: item.description,
      }));
      this.$emit('update', { descriptions: newDescriptions });
      this.descriptionsModalVisible = false;
    },
    // Image modal
    openImageModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.imageModalVisible = true;
      }
    },
    handleImageSave(values) {
      this.$emit('update', {
        imageSrc: values.image,
        imageSrcMobile: values.imageMobile,
      });
      this.imageModalVisible = false;
    },
    handleImageDelete() {
      this.$emit('update', {
        imageSrc: '',
        imageSrcMobile: '',
      });
      this.imageModalVisible = false;
    },
    // Link modal
    openLinkModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.linkModalVisible = true;
      }
    },
    handleLinkSave(values) {
      this.$emit('update', {
        linkText: values.buttonText,
        linkUrl: values.buttonLink,
        withLink: !!values.buttonText,
      });
      this.linkModalVisible = false;
    },
    handleLinkDelete() {
      this.$emit('update', {
        linkText: '',
        linkUrl: '',
        withLink: false,
      });
      this.linkModalVisible = false;
    },
  },
};
</script>

<template>
  <div :class="b('container', { left: isLeft, isMobileView, isEditMode })">
    <div :class="b('content', { full: true, isMobileView })">
      <!-- Title -->
      <div
        :class="b('title-wrap', { isMobileView, editable: isEditMode })"
        :role="isEditMode ? 'button' : undefined"
        :tabindex="isEditMode ? 0 : undefined"
        @click="openTitleModal"
        @keyup.enter="openTitleModal"
        @keyup.space="openTitleModal"
      >
        <h3 v-if="displayTitle" :class="b('title', { isMobileView })">
          {{ displayTitle }}
        </h3>
        <div v-else-if="isEditMode" :class="b('add-field')">
          + Add title
        </div>
        <PencilIcon
          v-if="isEditMode && displayTitle"
          :class="b('edit-icon')"
          :color="isMobileView ? '#E8B298' : '#293241'"
          :size="18"
        />
      </div>

      <!-- Descriptions -->
      <div
        :class="b('description-wrap', { isMobileView, editable: isEditMode })"
        :role="isEditMode ? 'button' : undefined"
        :tabindex="isEditMode ? 0 : undefined"
        @click="openDescriptionsModal"
        @keyup.enter="openDescriptionsModal"
        @keyup.space="openDescriptionsModal"
      >
        <template v-if="descriptions && descriptions.length > 0">
          <div
            v-for="description in descriptions"
            :key="description.id"
            :class="b('description', { isMobileView })"
          >
            <h4 v-if="description.title" :class="b('description-title', { isMobileView })">
              {{ description.title }}
            </h4>
            <p v-if="description.text" :class="b('description-text', { isMobileView })">
              {{ description.text }}
            </p>
          </div>
        </template>
        <div v-else-if="isEditMode" :class="b('add-field')">
          + Add descriptions
        </div>
        <PencilIcon
          v-if="isEditMode && descriptions && descriptions.length > 0"
          :class="b('edit-icon', { descriptions: true })"
          :color="isMobileView ? '#ffffff' : '#5e5d5d'"
          :size="18"
        />
      </div>

      <!-- Link -->
      <div
        v-if="hasLink || isEditMode"
        :class="b('link-wrap', { isMobileView, editable: isEditMode })"
        :role="isEditMode ? 'button' : undefined"
        :tabindex="isEditMode ? 0 : undefined"
        @click.prevent="openLinkModal"
        @keyup.enter="openLinkModal"
        @keyup.space="openLinkModal"
      >
        <a
          v-if="hasLink"
          :href="isEditMode ? undefined : linkUrl"
          :class="b('link', { isMobileView })"
        >
          {{ linkText }}
        </a>
        <div v-else-if="isEditMode" :class="b('add-field')">
          + Add link
        </div>
        <PencilIcon
          v-if="isEditMode && hasLink"
          :class="b('edit-icon')"
          :color="isMobileView ? '#ffffff' : '#5f8aa4'"
          :size="16"
        />
      </div>
    </div>

    <!-- Image -->
    <div
      :class="b('image-container', { left: isLeft, isMobileView, editable: isEditMode })"
      :role="isEditMode ? 'button' : undefined"
      :tabindex="isEditMode ? 0 : undefined"
      @click="openImageModal"
      @keyup.enter="openImageModal"
      @keyup.space="openImageModal"
    >
      <img
        v-if="imageSrc"
        :src="imageSrc"
        alt="Track Anytime"
        :class="b('image', { isMobileView })"
      />
      <div
        v-else-if="isEditMode"
        :class="b('media-placeholder', { isMobileView })"
      >
        <span :class="b('placeholder-plus')">+</span>
        CLICK TO UPLOAD
      </div>
      <div
        v-else
        :class="b('placeholder', { isMobileView })"
      />
    </div>

    <!-- Modals -->
    <EditTitleModal
      :visible="titleModalVisible"
      :title="title || ''"
      :title-mobile="titleMob || title || ''"
      modal-title="Edit Section Title"
      @close="titleModalVisible = false"
      @save="handleTitleSave"
      @delete="handleTitleDelete"
    />

    <EditSubtitlesModal
      :visible="descriptionsModalVisible"
      :data="subtitlesData"
      @save="handleDescriptionsSave"
      @cancel="descriptionsModalVisible = false"
    />

    <ImageUploadModal
      :visible="imageModalVisible"
      :image="imageSrc || ''"
      :image-mobile="imageSrcMobile || ''"
      :show-delete="!!imageSrc"
      modal-title="Edit Section Image"
      @close="imageModalVisible = false"
      @save="handleImageSave"
      @delete="handleImageDelete"
    />

    <EditButtonModal
      :visible="linkModalVisible"
      :button-text="linkText || ''"
      :button-text-mobile="linkText || ''"
      :button-link="linkUrl || ''"
      @close="linkModalVisible = false"
      @save="handleLinkSave"
      @delete="handleLinkDelete"
    />
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';
@import '@/styles/media.scss';

.track-anytime-tab {
  & * {
    font-family: Montserrat, sans-serif;
  }

  &__container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 32px 32px 32px 74px;
    gap: 66px;

    @include mediaMinMax(1025, $normal) {
      padding: vw-full(32) vw-full(32) vw-full(32) vw-full(74);
      gap: vw-full(66);
    }

    @include media($small) {
      flex-direction: column-reverse;
      padding: 0;
      gap: 0;
      margin-bottom: vw-mobile(28, 412);
      position: relative;
    }

    &_left {
      flex-direction: row-reverse;
      padding: 32px 74px 32px 32px;

      @include mediaMinMax(1025, $normal) {
        padding: vw-full(32) vw-full(74) vw-full(32) vw-full(32);
      }

      @include media($small) {
        flex-direction: column-reverse;
        padding: 0;
        gap: 0;
        margin-bottom: vw-mobile(28, 412);
      }

      &_isMobileView {
        flex-direction: column-reverse;
        padding: 0;
        gap: 0;
        margin-bottom: 28px;
      }
    }

    &_isMobileView {
      flex-direction: column-reverse;
      padding: 0;
      gap: 0;
      margin-bottom: 28px;
      position: relative;
    }
  }

  &__edit-icon {
    flex-shrink: 0;
    opacity: 0.8;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 1;
    }
  }

  &__add-field {
    @include add-field-button;

    &_small {
      font-size: 14px;
      padding: 6px 12px;

      @include mediaMinMax(1025, $normal) {
        font-size: vw-full(14);
        padding: vw-full(6) vw-full(12);
      }
    }
  }

  &__title-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    margin-bottom: 23px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(10);
      margin-bottom: vw-full(23);
    }

    @include media($small) {
      margin-bottom: 0;
      transform: translateY(-50%);
      z-index: 2;
    }

    &_isMobileView {
      margin-bottom: 0;
      transform: translateY(-50%);
      z-index: 2;
      height: 35px;
    }

    &_editable {
      cursor: pointer;
      padding: 5px 10px;
      margin: -5px -10px;
      border-radius: 5px;
      transition: background 0.2s ease;

      @include mediaMinMax(1025, $normal) {
        padding: vw-full(5) vw-full(10);
        margin: vw-full(-5) vw-full(-10);
        border-radius: vw-full(5);
      }

      &:hover {
        background: rgba(0, 0, 0, 0.05);

        .track-anytime-tab__edit-icon {
          opacity: 1;
        }
      }

      &_isMobileView:hover {
        background: rgba(255, 255, 255, 0.1);
      }
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    width: 540px;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(540);
    }

    @include media($small) {
      position: relative;
    }

    &_isMobileView {
      position: relative;
    }

    &_full {
      width: 100%;
      align-items: center;

      @include media($small) {
        width: 100%;
        align-items: center;
      }

      &_isMobileView {
        width: 100%;
        align-items: center;
      }
    }
  }

  &__image-container {
    min-width: 828px;
    height: 544px;
    border-radius: 12px 3px;
    background: #d9d9d9;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;

    @include mediaMinMax(1025, $normal) {
      border-radius: vw-full(12) vw-full(3);
      min-width: vw-full(828);
      height: vw-full(544);
    }

    @include media($small) {
      min-width: vw-mobile(356, 412);
      height: vw-mobile(232, 412);
      border-radius: vw-mobile(25, 412) vw-mobile(26, 412) 0 0;
      position: relative;

      &::after {
        content: '';
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(
          180deg,
          rgba(41, 50, 65, 0) 69.29%,
          #293241 98.97%
        );
      }
    }

    &_isMobileView {
      min-width: 356px;
      height: 232px;
      border-radius: 25px 26px 0 0;
      position: relative;

      &::after {
        content: '';
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(
          180deg,
          rgba(41, 50, 65, 0) 69.29%,
          #293241 98.97%
        );
      }
    }

    &_left {
      border-radius: 3px 12px;

      @include mediaMinMax(1025, $normal) {
        border-radius: vw-full(3) vw-full(12);
      }

      @include media($small) {
        min-width: vw-mobile(356, 412);
        height: vw-mobile(232, 412);
        border-radius: vw-mobile(25, 412) vw-mobile(26, 412) 0 0;
        position: relative;
      }

      &_isMobileView {
        min-width: 356px;
        height: 232px;
        border-radius: 25px 26px 0 0;
        position: relative;
      }
    }

    &_editable {
      cursor: pointer;

      &:hover {
        .track-anytime-tab__image {
          filter: brightness(0.7);
        }
      }
    }
  }

  &__media-placeholder {
    width: 100%;
    height: 100%;
    background: #d9d9d9;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #7f7f7f;
    font-family: Montserrat, sans-serif;
    font-size: 31px;
    font-weight: 400;
    text-transform: uppercase;
    text-align: center;
    line-height: 1.4;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(31);
      gap: vw-full(8);
    }
  }

  &__placeholder-plus {
    font-size: 72px;
    line-height: 1;
    font-weight: 200;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(72);
    }
  }

  &__placeholder {
    width: 100%;
    height: 100%;
    background-color: #eaeaea;
    min-height: 300px;

    @include mediaMinMax(1025, $normal) {
      min-height: vw-full(300);
    }

    @media (max-width: 768px) {
      min-height: 240px;
    }

    &_isMobileView {
      min-height: 240px;
    }
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: filter 0.2s ease;
  }

  &__title {
    color: $ebony-clay-gray-color;
    font-size: 36px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    margin-bottom: 23px;
    text-transform: uppercase;
    white-space: pre-wrap;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(36);
    }

    @include media($small) {
      width: 100%;
      text-align: center;
      color: $tacao;
      font-size: vw-mobile(20, 412);
      font-weight: 600;
      line-height: normal;
      text-transform: uppercase;
    }

    &_isMobileView {
      width: 100%;
      text-align: center;
      color: $tacao;
      font-size: 20px;
      font-weight: 600;
      line-height: normal;
      text-transform: initial;
    }
  }

  &__description-wrap {
    display: flex;
    flex-direction: column;
    gap: 32px;
    position: relative;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(32);
    }

    @include media($small) {
      align-items: center;
      gap: vw-mobile(28, 412);
    }

    &_isMobileView {
      align-items: center;
      gap: 28px;
    }

    &_editable {
      cursor: pointer;
      padding: 10px 15px;
      margin: -10px -15px;
      border-radius: 8px;
      transition: background 0.2s ease;

      @include mediaMinMax(1025, $normal) {
        padding: vw-full(10) vw-full(15);
        margin: vw-full(-10) vw-full(-15);
        border-radius: vw-full(8);
      }

      &:hover {
        background: rgba(0, 0, 0, 0.05);

        .track-anytime-tab__edit-icon {
          opacity: 1;
        }
      }

      &_isMobileView:hover {
        background: rgba(255, 255, 255, 0.1);
      }
    }
  }

  &__edit-icon {
    &_descriptions {
      position: absolute;
      top: 10px;
      right: 10px;

      @include mediaMinMax(1025, $normal) {
        top: vw-full(10);
        right: vw-full(10);
      }
    }
  }

  &__description {
    display: flex;
    flex-direction: column;
    gap: 16px;

    @include media($small) {
      align-items: center;
      gap: vw-mobile(16, 412);
      max-width: vw-mobile(316, 412);
    }

    &_isMobileView {
      align-items: center;
      gap: 16px;
      max-width: 316px;
    }
  }

  &__description-title {
    color: $horizon;
    font-size: 22px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    text-transform: uppercase;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(22);
    }

    @include media($small) {
      font-size: vw-mobile(20, 412);
      color: $white-color;
      text-align: center;
    }

    &_isMobileView {
      font-size: 20px;
      color: $white-color;
      text-align: center;
    }
  }

  &__description-text {
    color: $scorpion;
    font-size: 19px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    white-space: pre-wrap;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(19);
    }

    @include media($small) {
      font-size: vw-mobile(16, 412);
      color: $white-color;
      text-align: center;
    }

    &_isMobileView {
      font-size: 16px;
      color: $white-color;
      text-align: center;
    }
  }

  &__link-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin: 32px auto 0;
    text-align: center;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(8);
      margin: vw-full(32) auto 0;
    }

    &_editable {
      cursor: pointer;
      padding: 5px 10px;
      margin-top: 27px;
      border-radius: 5px;
      transition: background 0.2s ease;

      @include mediaMinMax(1025, $normal) {
        padding: vw-full(5) vw-full(10);
        margin-top: vw-full(27);
        border-radius: vw-full(5);
      }

      &:hover {
        background: rgba(0, 0, 0, 0.05);

        .track-anytime-tab__edit-icon {
          opacity: 1;
        }
      }

      &_isMobileView:hover {
        background: rgba(255, 255, 255, 0.1);
      }
    }
  }

  &__link {
    font-size: 19px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-skip-ink: none;
    text-decoration-thickness: auto;
    text-underline-offset: auto;
    text-underline-position: from-font;
    color: $horizon;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(19);
    }
  }
}
</style>
